import React, { useCallback, useEffect } from "react";
import ConsumeBlenderHub from "../../app/signalr/ConsumeBlenderHub";
import {
  IAgentClientState,
  IAgentStateTransitionRequested,
} from "../../models/BlenderAgentStateModel";
import { getEnvSettings } from "../../app/hostBasedSettings";
import { loadFragments } from "../../app/fragmentIntegration";
import { ThemeProvider } from "styled-components";
import { defaultColorTheme } from "../../style/theme";
import useMediaQuery from "../../app/useMediaQuery";
import PanelsContainer from "../../components/containers/PanelsContainer";
import TaskListContainer from "../../components/containers/TaskListContainer";
import TaskManagerContainer from "../../components/containers/TaskManagerContainer";
import PerformanceDashboardContainer from "../../components/containers/fragments/PerformanceDashboardContainer";
import MainLayout from "../../components/layouts/MainLayout";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setLoggedInStatus } from "../../redux/apis/blenderApiSlice";
import {
  toggleTaskManagerOpen,
  setSelectedWorkItemId,
  setMobileView,
} from "../../redux/slices/consolePageSlice";
import {
  updateStateDataRetrieved,
  updateMessageState,
} from "../../redux/slices/consumeBlenderHubSlice";

function ConsolePage() {
  const dispatch = useAppDispatch();

  const onGettingStatus = useCallback(
    (agentClientState: IAgentClientState) => {
      dispatch(updateStateDataRetrieved(agentClientState));
      if (agentClientState.agentProperties.state === "LoggedOut") {
        dispatch(setLoggedInStatus(false));
      }
      const afterWorkWI = Object.values(agentClientState.workItems).find(
        (wi) => wi.workItemState === "Afterwork"
      );
      if (afterWorkWI) {
        dispatch(toggleTaskManagerOpen());
        dispatch(setSelectedWorkItemId(afterWorkWI.workItemId));
      } else {
        const lookupWI = Object.values(agentClientState.workItems).find(
          (wi) => wi.workItemState === "LookUp"
        );
        if (lookupWI)
        {
          dispatch(setSelectedWorkItemId(lookupWI.workItemId));
        }
      }
    },
    [dispatch]
  );

  const onStateTransitionRequested = useCallback(
    (agentStateTransition: IAgentStateTransitionRequested) => {
      dispatch(
        updateMessageState({
          id: new Date().getTime(),
          timeStamp: new Date().toDateString(),
          text:
            "Supervisor requested the following state: " +
            agentStateTransition.toState,
          link: "",
        })
      );
    },
    [dispatch]
  );

  const blenderHub = ConsumeBlenderHub({
    onGettingStatus: onGettingStatus,
    onStateTransitions: onStateTransitionRequested,
  });

  const { isDashboardPanelOpen, isTaskListCollapsed, isTaskManagerOpen } =
    useAppSelector((state) => state.consolePageState);

  const workItemsCount = useAppSelector((state) => state.blenderState.workItemsCount)

  useEffect(
    () => {
      if (workItemsCount >= 0) {
        window.notifyQueueCount(workItemsCount);
      }
    },
    [workItemsCount],
  );

  useEffect(() => {
    blenderHub.start();

    const envSettings = getEnvSettings();
    loadFragments(envSettings);
  }, [blenderHub]);

  const handleViewportChanges = useCallback(
    (isMatch: boolean) => {
      dispatch(setMobileView(isMatch));
    },
    [dispatch]
  );

  useMediaQuery("(max-width: 1200px)", handleViewportChanges);

  return (
    <ThemeProvider theme={defaultColorTheme}>
      <MainLayout>
        {{
          sidebar: <TaskListContainer />,
          taskManager: <TaskManagerContainer />,
          content: <PanelsContainer />,
          performanceDashboard: (
            <PerformanceDashboardContainer />
          ),
          isDashboardPanelOpen: isDashboardPanelOpen,
          isTaskListCollapsed: isTaskListCollapsed,
          isTaskManagerOpen: isTaskManagerOpen,
        }}
      </MainLayout>
    </ThemeProvider>
  );
}

export default ConsolePage;
